import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Droplet, Trash2, ChevronRight, ChevronDown,
  FolderOpen, Folder, Loader2, AlertTriangle, Eye, Search,
} from 'lucide-react';
import { formatPeriod } from '../../utils/formatters';

/**
 * Lista bollette caricate, raggruppate per lead source in cartelle espandibili.
 * Ogni cartella ha una barra di ricerca per filtrare per nome cliente o POD/PDR.
 */
export const BillsList = ({
  groupedBills, openFolders, onToggleFolder,
  selectedBill, analyzingBillId, hasCustomerType,
  analyzedBillIds,
  analysesCountByBillId,
  onAnalyze, onDelete,
}) => {
  if (groupedBills.length === 0) return null;

  const totalBills = groupedBills.reduce((sum, g) => sum + g.bills.length, 0);

  return (
    <div className="el-card p-6" data-testid="bills-grouped-list">
      <h3 className="el-label mb-4">Bollette Caricate ({totalBills})</h3>
      <div className="space-y-2">
        {groupedBills.map((group) => (
          <BillsFolder
            key={group.key}
            group={group}
            isOpen={!!openFolders[group.key]}
            onToggle={() => onToggleFolder(group.key)}
            selectedBill={selectedBill}
            analyzingBillId={analyzingBillId}
            hasCustomerType={hasCustomerType}
            analyzedBillIds={analyzedBillIds}
            analysesCountByBillId={analysesCountByBillId}
            onAnalyze={onAnalyze}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

/** Singola cartella bollette con ricerca interna per nome cliente / POD-PDR. */
const BillsFolder = ({
  group, isOpen, onToggle,
  selectedBill, analyzingBillId, hasCustomerType,
  analyzedBillIds, analysesCountByBillId,
  onAnalyze, onDelete,
}) => {
  const [query, setQuery] = useState('');

  const filteredBills = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return group.bills;
    return group.bills.filter((b) => {
      const ed = b.extracted_data || {};
      const name = (ed.client_name || b.filename || '').toLowerCase();
      const sii = (ed.sii_code || '').toLowerCase();
      return name.includes(q) || sii.includes(q);
    });
  }, [group.bills, query]);

  return (
    <div className="border border-[#DCE3DC] rounded-xl overflow-hidden" data-testid={`folder-${group.key}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 bg-[#F4F7F4] hover:bg-[#E8F5E9] transition-colors text-left"
        data-testid={`folder-toggle-${group.key}`}
      >
        {isOpen ? (
          <FolderOpen className="w-5 h-5 text-[#4CAF50] flex-shrink-0" />
        ) : (
          <Folder className="w-5 h-5 text-[#5C6E5C] flex-shrink-0" />
        )}
        <span className="font-medium text-sm text-[#1B291B] flex-1">{group.label}</span>
        <span className="text-xs text-[#5C6E5C] bg-white px-2 py-0.5 rounded-full border border-[#DCE3DC]">
          {group.bills.length}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#5C6E5C] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2 space-y-2 bg-white">
              {group.bills.length > 3 && (
                <FolderSearchInput
                  value={query}
                  onChange={setQuery}
                  placeholder="Cerca per nome cliente o POD/PDR..."
                  testId={`folder-search-${group.key}`}
                />
              )}
              {filteredBills.length === 0 ? (
                <p className="text-xs text-[#5C6E5C] text-center py-3 italic">
                  Nessuna bolletta corrisponde a &ldquo;{query}&rdquo;
                </p>
              ) : (
                filteredBills.map((bill) => (
                  <BillItem
                    key={bill.id}
                    bill={bill}
                    isSelected={selectedBill?.id === bill.id}
                    isAnalyzing={analyzingBillId === bill.id}
                    hasCustomerType={hasCustomerType}
                    hasExistingAnalysis={analyzedBillIds?.has(bill.id)}
                    analysesCount={analysesCountByBillId?.[bill.id] || 0}
                    onAnalyze={onAnalyze}
                    onDelete={onDelete}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/** Input di ricerca riusabile per filtrare i contenuti di una cartella. */
export const FolderSearchInput = ({ value, onChange, placeholder, testId }) => (
  <div className="relative" data-testid={testId}>
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6E5C]" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#DCE3DC] rounded-md bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#4CAF50]/30 focus:border-[#4CAF50]"
      data-testid={`${testId}-input`}
    />
  </div>
);

/** Singola bolletta nella lista */
const BillItem = ({ bill, isSelected, isAnalyzing, hasCustomerType, hasExistingAnalysis, analysesCount, onAnalyze, onDelete }) => {
  const isProcessing = bill.status === 'processing';
  const extractionFailed = bill.status === 'extraction_failed';
  // La bolletta puo' essere aperta se: estrazione completata + tipologia cliente disponibile
  // (dal box globale OPPURE gia' presente sulla bolletta — caso "riapro una bolletta di ieri").
  const billHasCustomerType = !!bill.extracted_data?.customer_type;
  const canOpen = !isProcessing && !extractionFailed && (hasCustomerType || billHasCustomerType);
  const btnTitle = isProcessing
    ? 'Estrazione AI in corso...'
    : extractionFailed
      ? 'Estrazione fallita - riprova'
      : canOpen ? 'Apri / Analizza' : 'Seleziona tipologia cliente per analizzare';

  return (
    <div
      className={`p-3 rounded-lg border transition-all cursor-pointer ${
        isSelected ? 'border-[#4CAF50] bg-[#E8F5E9]' : 'border-[#DCE3DC] hover:border-[#4CAF50]/50'
      } ${isProcessing ? 'opacity-80' : ''}`}
      data-testid={`bill-item-${bill.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {isProcessing ? (
            <Loader2 className="w-5 h-5 text-[#4CAF50] animate-spin" />
          ) : bill.extracted_data?.service_type === 'gas' ? (
            <Droplet className="w-5 h-5 text-sky-400 fill-sky-400" />
          ) : (
            <Zap className="w-5 h-5 text-yellow-500" />
          )}
          <div>
            <p className="font-medium text-[#1B291B] text-sm">
              {isProcessing ? bill.filename : (bill.extracted_data?.client_name || bill.filename)}
            </p>
            {isProcessing ? (
              <p className="text-xs text-[#4CAF50] mt-1 italic" data-testid={`bill-processing-${bill.id}`}>
                Estrazione AI in corso...
              </p>
            ) : extractionFailed ? (
              <p className="text-xs text-red-600 mt-1" data-testid={`bill-failed-${bill.id}`}>
                Estrazione fallita - elimina e riprova
              </p>
            ) : (
              <>
                <p className="text-xs text-[#5C6E5C] mt-1">
                  {bill.extracted_data?.consumption} {bill.extracted_data?.consumption_unit || 'kWh'}
                  {bill.extracted_data?.billing_period_start && (
                    <> &bull; {formatPeriod(bill.extracted_data.billing_period_start, bill.extracted_data.billing_period_end)}</>
                  )}
                  {bill.extracted_data?.sii_code && (
                    <> &bull; <span className="font-medium">{bill.extracted_data?.service_type === 'gas' ? 'PDR' : 'POD'}:</span> {bill.extracted_data.sii_code}</>
                  )}
                </p>
                {bill.extracted_data?.is_fixed_price && (
                  <span className="inline-flex items-center gap-1 text-xs text-orange-600 mt-1">
                    <AlertTriangle className="w-3 h-3" /> Prezzo Fisso
                  </span>
                )}
                {hasExistingAnalysis && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#4CAF50] mt-1" data-testid={`bill-analyzed-${bill.id}`}>
                    <Eye className="w-3 h-3" /> {analysesCount > 1 ? `${analysesCount} analisi finalizzate` : '1 analisi finalizzata'}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onAnalyze(bill)}
            disabled={isAnalyzing || !canOpen}
            className="p-2 rounded-lg bg-[#4CAF50] text-white hover:bg-[#3D8C40] transition-colors disabled:opacity-50"
            data-testid={`analyze-btn-${bill.id}`}
            title={btnTitle}
          >
            {(isAnalyzing || isProcessing) ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onDelete(bill.id)}
            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
            data-testid={`delete-btn-${bill.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
